import { useEffect, useMemo, useState } from "react";
import { v2API } from "./v2Api";
import { toast } from "sonner";
import {
  ShieldCheck, MoreVertical, KeyRound, Lock, Unlock, UserCheck, UserX,
  Copy, RefreshCw, Trash2, ShieldOff, ShieldAlert, CheckCircle2,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const STATUS_TONE = {
  active:   "bg-emerald-100 text-emerald-800 border-emerald-200",
  pending:  "bg-amber-100 text-amber-800 border-amber-200",
  disabled: "bg-slate-200 text-slate-700 border-slate-300",
  locked:   "bg-rose-100 text-rose-800 border-rose-200",
  no_login: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function SystemAccess() {
  const [rows, setRows]           = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [pwdRow, setPwdRow] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [pwdReason, setPwdReason] = useState("Admin reset");

  const [roleRow, setRoleRow] = useState(null);
  const [newTpl, setNewTpl]   = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [sa, tpls] = await Promise.all([v2API.systemAccess(), v2API.roleTemplates()]);
      setRows(sa); setTemplates(tpls);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.identity_status !== statusFilter) return false;
      if (search && !`${r.employee_name} ${r.email} ${r.department}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [rows, search, statusFilter]);

  const doAction = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="wf-system-access">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-[20px] font-semibold text-hocp-navy flex items-center gap-2">
            <ShieldCheck className="w-5 h-5"/> System Access
          </h1>
          <p className="text-[12px] text-slate-500">
            Manages login accounts &amp; role assignments. Employees without login are managed under Employees.
          </p>
        </div>
        <div className="text-[11px] uppercase tracking-wider text-slate-400">{filtered.length} / {rows.length}</div>
      </div>

      <div className="flex items-center gap-3">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / email / department…" aria-label="Search system users"
          className="h-9 flex-1 max-w-md text-[13px]" data-testid="sa-search"/>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-[160px] text-[13px]" data-testid="sa-status-filter" aria-label="Filter by status"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending Activation</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
            <SelectItem value="locked">Locked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2">Employee</th>
              <th className="text-left px-4 py-2">Username</th>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2">Department</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Last Login</th>
              <th className="text-center px-4 py-2 w-[80px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">No system users match.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.user_id} data-testid={`sa-row-${r.user_id}`}>
                <td className="px-4 py-2 font-medium text-hocp-navy">{r.employee_name}
                  {r.is_super_admin && <span className="ml-1 text-[10.5px] uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Super Admin</span>}
                </td>
                <td className="px-4 py-2 text-slate-600">{r.email}</td>
                <td className="px-4 py-2">{r.role_template_name || <span className="text-slate-400 italic">—</span>}</td>
                <td className="px-4 py-2">{r.department || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded-md border text-[10.5px] font-medium uppercase tracking-wider ${STATUS_TONE[r.identity_status] || STATUS_TONE.active}`} data-testid={`sa-status-${r.user_id}`}>
                    {(r.identity_status || "active").replace("_"," ")}
                  </span>
                  {r.failed_login_count > 0 && (
                    <span className="ml-1 text-[10.5px] text-rose-600" title={`${r.failed_login_count} failed`}>· {r.failed_login_count} failed</span>
                  )}
                </td>
                <td className="px-4 py-2 text-slate-500 text-[12px]">{r.last_login_at ? new Date(r.last_login_at).toLocaleString() : <span className="text-slate-400">Never</span>}</td>
                <td className="px-4 py-2 text-center">
                  {r.is_super_admin ? (
                    <span className="text-slate-300"><Lock className="w-4 h-4 mx-auto"/></span>
                  ) : (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1.5 rounded hover:bg-slate-100" data-testid={`sa-menu-${r.user_id}`}><MoreVertical className="w-4 h-4 text-slate-500"/></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        {r.identity_status === "pending" && (
                          <DropdownMenuItem onClick={() => doAction(() => v2API.activate(r.user_id), "Activated")}
                            data-testid={`sa-activate-${r.user_id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-2 text-emerald-600"/> Activate
                          </DropdownMenuItem>
                        )}
                        {r.identity_status === "active" && (
                          <DropdownMenuItem onClick={() => doAction(() => v2API.disable(r.user_id), "Disabled")}
                            data-testid={`sa-disable-${r.user_id}`}>
                            <UserX className="w-3.5 h-3.5 mr-2 text-rose-600"/> Disable
                          </DropdownMenuItem>
                        )}
                        {r.identity_status === "disabled" && (
                          <DropdownMenuItem onClick={() => doAction(() => v2API.activate(r.user_id), "Re-enabled")}
                            data-testid={`sa-enable-${r.user_id}`}>
                            <UserCheck className="w-3.5 h-3.5 mr-2 text-emerald-600"/> Re-enable
                          </DropdownMenuItem>
                        )}
                        {(r.identity_status === "locked" || r.failed_login_count >= 5) && (
                          <DropdownMenuItem onClick={() => doAction(() => v2API.unlock(r.user_id), "Unlocked")}
                            data-testid={`sa-unlock-${r.user_id}`}>
                            <Unlock className="w-3.5 h-3.5 mr-2 text-sky-600"/> Unlock
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator/>
                        <DropdownMenuItem onClick={() => { setPwdRow(r); setNewPwd(""); }}
                          data-testid={`sa-reset-${r.user_id}`}>
                          <KeyRound className="w-3.5 h-3.5 mr-2"/> Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setRoleRow(r); setNewTpl(""); }}
                          data-testid={`sa-change-role-${r.user_id}`}>
                          <ShieldAlert className="w-3.5 h-3.5 mr-2"/> Change Role
                        </DropdownMenuItem>
                        <DropdownMenuSeparator/>
                        <DropdownMenuItem className="text-rose-600" onClick={() => doAction(() => v2API.removeAccess(r.user_id), "Access removed")}
                          data-testid={`sa-remove-${r.user_id}`}>
                          <ShieldOff className="w-3.5 h-3.5 mr-2"/> Remove Access
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Reset password dialog */}
      <Dialog open={!!pwdRow} onOpenChange={(v) => !v && setPwdRow(null)}>
        <DialogContent data-testid="sa-reset-dialog">
          <DialogHeader><DialogTitle>Reset password — {pwdRow?.employee_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label htmlFor="sa-reset-pwd" className="block text-[11.5px] font-medium text-slate-600 mb-1">New temporary password</label>
              <Input id="sa-reset-pwd" type="text" placeholder="Min 8 characters" value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)} data-testid="sa-reset-pwd"/>
            </div>
            <div>
              <label htmlFor="sa-reset-reason" className="block text-[11.5px] font-medium text-slate-600 mb-1">Reason</label>
              <Input id="sa-reset-reason" value={pwdReason} onChange={(e) => setPwdReason(e.target.value)} placeholder="e.g. Admin reset"/>
            </div>
            <div className="text-[11.5px] text-slate-500">The employee will be forced to change this on their next login.</div>
          </div>
          <DialogFooter>
            <button onClick={() => setPwdRow(null)} className="h-9 px-4 text-slate-500 text-[13px]">Cancel</button>
            <button disabled={newPwd.length < 8} onClick={async () => {
              await doAction(() => v2API.resetPassword(pwdRow.user_id, { new_password: newPwd, reason: pwdReason, force_password_change: true }),
                "Password reset");
              setPwdRow(null);
            }} className="h-9 px-4 rounded-md bg-hocp-mint text-hocp-deepTeal text-[13px] disabled:opacity-40" data-testid="sa-reset-confirm">Reset</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change role dialog */}
      <Dialog open={!!roleRow} onOpenChange={(v) => !v && setRoleRow(null)}>
        <DialogContent data-testid="sa-role-dialog">
          <DialogHeader><DialogTitle>Change role — {roleRow?.employee_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={newTpl} onValueChange={setNewTpl}>
              <SelectTrigger data-testid="sa-role-select" aria-label="Role template"><SelectValue placeholder="Pick a role template"/></SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.group} · {t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {newTpl && (
              <div className="text-[12px] p-3 bg-sky-50/60 border border-sky-200 rounded-md">
                {(() => {
                  const t = templates.find(x => x.id === newTpl);
                  if (!t) return null;
                  return <><div className="font-medium">{t.name}</div><div className="text-slate-600 mt-1">{(t.modules || []).join(", ")}</div><div className="text-slate-500 mt-1">{t.capability_count} capabilities</div></>;
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setRoleRow(null)} className="h-9 px-4 text-slate-500 text-[13px]">Cancel</button>
            <button disabled={!newTpl} onClick={async () => {
              await doAction(() => v2API.changeRole(roleRow.user_id, newTpl), "Role changed");
              setRoleRow(null);
            }} className="h-9 px-4 rounded-md bg-hocp-mint text-hocp-deepTeal text-[13px] disabled:opacity-40" data-testid="sa-role-confirm">Apply</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
